import { fromJS } from 'immutable';
import { DELIVERY_TYPES } from '../constants/constants';
import { isLocaleAllowed } from '../utils/landingPageUtils';

const hasBootstrapData = () => !!window.BOOTSTRAP_DATA;
const hasPreferredValues = () => !!window.BOOTSTRAP_DATA.preferredValues;



export const getEnv = () => {
    if (hasBootstrapData()) {
        return window.BOOTSTRAP_DATA.ENVIRONMENT;
    }
    return null;
};

export const getStaticAssetUrl = () => {
    if (hasBootstrapData()) {
        return window.BOOTSTRAP_DATA.staticAssetUrl;
    }
    return null;
};

export const getKlarnaClientId = () => {
    if (hasBootstrapData()) {
        return window.BOOTSTRAP_DATA.klarnaClientId;
    }
    return null;
};
export const getBrandData = () => {
    if (hasBootstrapData()) {
        return window.BOOTSTRAP_DATA.brand;
    }
    return {};
};

export const getBrandCode = () => {
    if (hasBootstrapData()) {
        return window.BOOTSTRAP_DATA.brand.brandCode;
    }
    return null;
};

export const getDefaultLocaleCode = () => {
    if (hasBootstrapData()) {
        return window.BOOTSTRAP_DATA.brand.defaultLocaleCode;
    }
    return null;
};

export const getBrandUrl = () => {
    if (hasBootstrapData()) {
        return window.BOOTSTRAP_DATA.brand.brandUrl;
    }
    return '';
};

/**
 * Get static media URL with optional suffix.
 * @param {string} [suffix] Optional path appended to the static media url.
 * @returns {string|undefined} If the current window does not have bootstrap data,
 *                             return undefined. Otherwise, return the formatted static media URL based on
 *                             bootstrap data and suffix parameter.
 */
export const getStaticMediaURL = (suffix) => {
    if (!hasBootstrapData()) {
        return null;
    }
    let url = window.BOOTSTRAP_DATA.staticMediaUrl;
    url = url.replace(/\/$/, '');

    if (suffix) {
        url += suffix;
    }
    return url;
};

/* Helper to get the static URL and provide a testing seam */
export const getStaticUrl = () => {
    if (window.BOOTSTRAP_DATA) {
        return window.BOOTSTRAP_DATA.staticUrl;
    }
    return undefined;
};

export const getCapiUrl = () => {
    if (hasBootstrapData()) {
        return window.BOOTSTRAP_DATA.capiUrl;
    }
    return null;
};

export const getOrderingToken = () => {
    if (hasBootstrapData()) {
        return window.BOOTSTRAP_DATA.brand.orderingToken;
    }
    return null;
};

export const getOrderingTokenServiceCodes = () => {
    if (hasBootstrapData()) {
        return window.BOOTSTRAP_DATA.brand.ordering_token_service_codes;
    }
    return null;
};

export const getSegmentKey = () => {
    try {
        // Obfuscate segment key by naming after moon of Saturn
        return window.BOOTSTRAP_DATA.brand.iapetus;
    } catch (e) {
        return null;
    }
};

const hasCookieConsentEnabled = () => (hasBootstrapData()
    && !!window.BOOTSTRAP_DATA.brand
    && !!window.BOOTSTRAP_DATA.brand.cookieConsentEnabled);

export const getCookieConsentEnabled = () => {
    if (hasCookieConsentEnabled()) {
        return window.BOOTSTRAP_DATA.brand.cookieConsentEnabled;
    }
    return null;
};

export const getShrinkEnabledInputField = () => {
    try {
        return window.BOOTSTRAP_DATA.brand.shrink_enabled_input_field;
    } catch (e) {
        return null;
    }
};

export const getExcludeBillingInfo = () => {
    if (hasBootstrapData()) {
        return window.BOOTSTRAP_DATA.brand.exclude_billing_info;
    }
    return false;
};

export const getPreferredValues = () => window.BOOTSTRAP_DATA.preferredValues;

export const getIsSmartflow = () => {
    if (hasPreferredValues()) {
        const { cardType } = getPreferredValues();
        if (cardType && (cardType.toLowerCase() === 'digital' || cardType.toLowerCase() === 'physical')) {
            return true;
        }
        return false;
    }
    return false;
};

export const getPreferredPromoCode = () => {
    if (hasPreferredValues()) {
        return getPreferredValues().promoCode;
    }
    return null;
};

export const getIsOldUiPreferred = () => {
    if (hasBootstrapData()) {
        return window.BOOTSTRAP_DATA.brand.OldUiPreferred;
    }
    return false;
};

export const getIsNewHomePageEnabled = () => {
    if (hasBootstrapData()) {
        return window.BOOTSTRAP_DATA.brand.newHomePageEnabled;
    }
    return false;
};

export const getAMGAbTestGroup = () => {
    if (hasBootstrapData()) {
        const group = Number(window.BOOTSTRAP_DATA.brand.abTestGroup);
        return group === 1 ? 1 : 0;
    }
    return 0;
};

export const getPhase2Preferred = () => {
    if (hasBootstrapData()) {
        return window.BOOTSTRAP_DATA.brand.phase2Preferred;
    }
    return false;
};

export const defaultGiftCardToggle = () => {
    if (hasBootstrapData() && window.BOOTSTRAP_DATA.brand.defaultGiftCardToggle) {
        return DELIVERY_TYPES.GIFT;
    }
    return DELIVERY_TYPES.SELF;
};

export const getAllowedLocaleList = () => {
    if (hasBootstrapData() && window.BOOTSTRAP_DATA.brand.allowedLocaleList) {
        return window.BOOTSTRAP_DATA.brand.allowedLocaleList;
    }
    return ['en-us'];
};

export const getPreferredCurrencyCode = () => {
    if (hasBootstrapData() && !getIsOldUiPreferred()) {
        const preferredCurrencyCode = getPreferredValues().currencyCode;
        
        if (preferredCurrencyCode !== 'BRL') {
            return preferredCurrencyCode;
        }
    }
    return null;
};

export const isGSAEnabled = () => {
    if (hasBootstrapData() && !getIsOldUiPreferred()) {
        const isGSA = (String(getPreferredValues()?.GSA ?? 'false').toLowerCase() === 'true');
        if (isGSA) {
            return true;
        }
    }
    return false;
};

export const getPreferredLocaleCode = () => {
    if (hasBootstrapData()) {
        const prefferedLocale = window.BOOTSTRAP_DATA.brand.preferredLocaleCode;
        if (isLocaleAllowed(prefferedLocale, getAllowedLocaleList())) {
            return prefferedLocale;
        }
    }
    return null;
};

export const getFooterLogosLinks = () => {
    if (hasBootstrapData()) {
        return window.BOOTSTRAP_DATA.footerLogosLinks;
    }
    return null;
};

export const getFeatureFlags = () => {
    if (hasBootstrapData()) {
        return fromJS(window.BOOTSTRAP_DATA.featureFlags);
    }
    return null;
};

export const getMultiRecipientEnabled = () => {
    if (hasBootstrapData()) {
        return fromJS(window.BOOTSTRAP_DATA.multiRecipientEnabled);
    }
    return null;
};

export const mockPaymentEnabled = () => {
    if (hasBootstrapData() && window.BOOTSTRAP_DATA.mockPaymentEnabledBrands) {
        const mockPaymentEnabledBrands = window.BOOTSTRAP_DATA.mockPaymentEnabledBrands.split(',');
        return mockPaymentEnabledBrands.includes(getBrandCode());
    }
    return false;
};

export const getEnableAbandonedCart = () => {
    if (hasBootstrapData() && window.BOOTSTRAP_DATA.enableAbandonedCartForBrands) {
        const enableAbandonedCartForBrands = window.BOOTSTRAP_DATA.enableAbandonedCartForBrands.split(',');
        return enableAbandonedCartForBrands.includes(getBrandCode());
    }
    return false;
};

export const getPassingPromoDetailsEnabled = () => (
    hasBootstrapData() && !!window.BOOTSTRAP_DATA.passingPromoDetails
);

export const isGreetingsEnabled = () => {
    if (hasBootstrapData() && window.BOOTSTRAP_DATA.brand) {
        return window.BOOTSTRAP_DATA.brand.greetingsEnabled;
    }
    return false;
};

export const getGreetingsPricePerCurrency = () => {
    if (hasBootstrapData() && window.BOOTSTRAP_DATA.brand) {
        return window.BOOTSTRAP_DATA.brand.greetingsPricePerCurrency;
    }
    return null;
};

export const getGreetingsProviderCode = () => {
    if (hasBootstrapData() && window.BOOTSTRAP_DATA.brand) {
        return window.BOOTSTRAP_DATA.brand.greetingsProviderCode;
    }
    return null;
};

export const getGreetingsProviderToken = () => {
    if (hasBootstrapData() && window.BOOTSTRAP_DATA.brand) {
        return window.BOOTSTRAP_DATA.brand.greetingsProviderToken;
    }
    return null;
};

export const getGreetingsSupportedLocales = () => {
    if (hasBootstrapData() && window.BOOTSTRAP_DATA.brand) {
        return window.BOOTSTRAP_DATA.brand.greetingsSupportedLocales;
    }
    return null;
};

export const getGreetingsPublicCdnUrl = () => {
    if (hasBootstrapData() && window.BOOTSTRAP_DATA.brand) {
        return window.BOOTSTRAP_DATA.brand.greetingsPublicCdnUrl;
    }
    return null;
};

export const getRealtimeRiskPagesEnableBrand = () => {
    if (hasBootstrapData() && window.BOOTSTRAP_DATA.brand) {
        return window.BOOTSTRAP_DATA.brand.isRealtimeRiskPagesEnabled;
    }
    return false;
};

export const getSSOEnabled = () => {
    if (hasBootstrapData() && window.BOOTSTRAP_DATA.brand) {
        return window.BOOTSTRAP_DATA.brand.ssoEnabled || false;
    }
    return false;
};

export const getUploadcarePublicKey = () => {
    if (hasBootstrapData()) {
        return window.BOOTSTRAP_DATA.uploadcarePublicKey;
    }
    return null;
};
